import React, { useState } from 'react';
import { complianceService } from '@/firebase/complianceService';
import { useNotification } from '@/contexts/NotificationContext';

const ComplianceImport: React.FC = () => {
  const { showNotification } = useNotification();
  const [jsonData, setJsonData] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<string>('');
  const [error, setError] = useState<string>('');

  const handleImport = async () => {
    setIsLoading(true);
    setResult('');
    setError('');

    try {
      // Parse JSON
      const data = JSON.parse(jsonData);
      
      // Validate structure
      if (!data.templates || !Array.isArray(data.templates)) {
        throw new Error('Invalid JSON format. Expected "templates" array.');
      }

      // Import templates
      const importResult = await complianceService.importTemplates(data.templates);
      
      if (importResult.success) {
        setResult(`✅ Successfully imported ${importResult.imported} templates. ${importResult.failed} failed.`);
        showNotification(`Successfully imported ${importResult.imported} templates`, 'success');
      } else {
        setError(`❌ Import failed: ${importResult.error}`);
        showNotification(`Import failed: ${importResult.error}`, 'error');
      }
    } catch (err: any) {
      setError(`❌ Error: ${err.message}`);
      showNotification(`Import error: ${err.message}`, 'error');
      console.error('Import error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setJsonData(content);
    };
    reader.onerror = () => {
      setError('Error reading file');
    };
    reader.readAsText(file);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-semibold text-gray-800 mb-4">Import Compliance Templates</h2>
      <p className="text-gray-600 mb-4">
        Upload JSON file or paste JSON data to import compliance templates into the database.
      </p>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Upload JSON File:
        </label>
        <input
          type="file"
          accept=".json"
          onChange={handleFileUpload}
          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
        />
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Or Paste JSON:
        </label>
        <textarea
          value={jsonData}
          onChange={(e) => setJsonData(e.target.value)}
          className="w-full h-64 p-3 border border-gray-300 rounded-md font-mono text-sm"
          placeholder="Paste your JSON here..."
        />
      </div>

      <div className="flex space-x-4 mb-4">
        <button
          onClick={handleImport}
          disabled={!jsonData || isLoading}
          className="btn-primary px-6 py-2"
        >
          {isLoading ? 'Importing...' : 'Import Templates'}
        </button>
        <button
          onClick={() => setJsonData('')}
          className="btn-secondary px-6 py-2"
        >
          Clear
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {result && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
          {result}
        </div>
      )}
    </div>
  );
};

export default ComplianceImport;

